import { ArrowRight, CheckCircle2, ClipboardList, ExternalLink, FolderKanban, Loader2, PackageCheck, Send } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  getProjectWorkspaceProject,
  getProjectWorkspaceProjects,
  submitProjectWorkspaceResult,
  updateProjectWorkspaceTask,
} from '../api.js'
import { useAuth } from '../auth-context.js'
import { setPageMetadata } from '../page-metadata.js'

function projectStages(project) {
  const stages = Array.isArray(project?.plan?.stages) ? project.plan.stages : []
  if (stages.length) return stages
  return [{ title: '执行阶段', goal: project?.projectGoal || project?.summary || '完成项目任务并沉淀成果。' }]
}

export default function StudentProjectWorkspace() {
  const auth = useAuth()
  const [state, setState] = useState({ loading: true, projects: [], selected: null, error: '' })
  const [resultForm, setResultForm] = useState({ title: '', content: '', url: '' })
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setPageMetadata({
      title: '项目成长工厂 - 青途 AI',
      description: '查看项目阶段和任务，更新进度，提交项目成果并沉淀成长资产。',
    })
  }, [])

  useEffect(() => {
    if (auth?.loading || !auth?.user) {
      setState({ loading: false, projects: [], selected: null, error: '' })
      return undefined
    }
    const controller = new AbortController()
    setState((current) => ({ ...current, loading: true, error: '' }))
    getProjectWorkspaceProjects(controller.signal)
      .then((result) => {
        const projects = Array.isArray(result.projects) ? result.projects : []
        setState({ loading: false, projects, selected: projects[0] || null, error: '' })
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return
        setState({ loading: false, projects: [], selected: null, error: error?.message || '项目成长工厂暂时无法加载' })
      })
    return () => controller.abort()
  }, [auth?.loading, auth?.user])

  const selectProject = async (project) => {
    setState((current) => ({ ...current, selected: project }))
    if (!project?.id) return
    try {
      const detail = await getProjectWorkspaceProject(project.id)
      setState((current) => ({ ...current, selected: detail.project || project }))
    } catch {
      setState((current) => ({ ...current, selected: project }))
    }
  }

  const updateTask = async (task, status) => {
    if (!state.selected?.id || !task?.id) return
    setSaving(true)
    try {
      const result = await updateProjectWorkspaceTask(state.selected.id, task.id, { status })
      setState((current) => ({
        ...current,
        selected: result.project || current.selected,
        projects: current.projects.map((project) => String(project.id) === String(state.selected.id) ? (result.project || project) : project),
      }))
    } finally {
      setSaving(false)
    }
  }

  const submitResult = async (event) => {
    event.preventDefault()
    if (!state.selected?.id || !resultForm.title.trim()) return
    setSaving(true)
    try {
      const result = await submitProjectWorkspaceResult(state.selected.id, {
        title: resultForm.title,
        content: resultForm.content,
        url: resultForm.url,
        resultType: 'workspace',
        progress: 80,
      })
      setState((current) => ({
        ...current,
        selected: result.project || current.selected,
        projects: current.projects.map((project) => String(project.id) === String(state.selected.id) ? (result.project || project) : project),
      }))
      setResultForm({ title: '', content: '', url: '' })
    } finally {
      setSaving(false)
    }
  }

  if (auth?.loading || state.loading) {
    return (
      <main className="page-container project-workspace-page">
        <section className="project-workspace-hero"><Loader2 size={18} /> 正在加载项目成长工厂...</section>
      </main>
    )
  }

  if (!auth?.user) {
    return (
      <main className="page-container project-workspace-page">
        <section className="project-workspace-hero">
          <span><FolderKanban size={16} /> 项目成长工厂</span>
          <h1>登录后管理你的项目实践</h1>
          <p>查看项目阶段和任务，提交成果，系统会把实践进度沉淀到成长资产库。</p>
          <Link className="primary-button" to="/login">去登录 <ArrowRight size={16} /></Link>
        </section>
      </main>
    )
  }

  const selected = state.selected
  const stages = projectStages(selected)

  return (
    <main className="page-container project-workspace-page">
      <section className="project-workspace-hero">
        <div>
          <span><FolderKanban size={16} /> 项目成长工厂</span>
          <h1>把学习路线推进成可展示项目</h1>
          <p>查看阶段、拆解任务、更新完成状态，并提交你的代码、文档或作品链接。</p>
        </div>
        <Link className="secondary-button" to="/projects/recommendations">查看项目库</Link>
      </section>

      {state.error ? <p className="student-growth-error">{state.error}</p> : null}

      <section className="project-workspace-layout">
        <aside className="project-list-panel">
          <header><h2>我的项目</h2><PackageCheck size={17} /></header>
          {state.projects.length ? state.projects.map((project) => (
            <button key={project.id} type="button" className={String(selected?.id) === String(project.id) ? 'active' : ''} onClick={() => selectProject(project)}>
              <strong>{project.title}</strong>
              <span>{project.status || 'active'} · {project.progress || 0}%</span>
            </button>
          )) : (
            <div className="empty-state-card">
              暂无项目。可以从学习路线或项目库生成一个项目计划。
            </div>
          )}
        </aside>

        <section className="project-work-panel">
          {selected ? (
            <>
              <header className="project-work-header">
                <span>{selected.status || 'active'} · {selected.progress || 0}%</span>
                <h2>{selected.title}</h2>
                <p>{selected.projectGoal || selected.summary}</p>
              </header>

              <div className="project-stage-grid">
                {stages.map((stage, index) => (
                  <article key={`${stage.title}-${index}`}>
                    <small>阶段 {index + 1}</small>
                    <strong>{stage.title}</strong>
                    <p>{stage.goal || stage.description || '完成本阶段任务。'}</p>
                  </article>
                ))}
              </div>

              <section className="project-task-panel">
                <header><ClipboardList size={18} /><h3>项目任务</h3></header>
                {(selected.tasks || []).length ? selected.tasks.map((task) => (
                  <article key={task.id}>
                    <div>
                      <strong>{task.title}</strong>
                      <span>{task.type || 'project'} · {task.status || 'pending'}</span>
                    </div>
                    <button type="button" onClick={() => updateTask(task, ['done', 'completed'].includes(task.status) ? 'pending' : 'done')} disabled={saving}>
                      <CheckCircle2 size={16} />
                      {['done', 'completed'].includes(task.status) ? '标记待办' : '标记完成'}
                    </button>
                  </article>
                )) : <p>暂无拆解任务。可以先提交阶段成果，后续继续补充任务。</p>}
              </section>

              <form className="project-result-form" onSubmit={submitResult}>
                <h3>提交成果</h3>
                <input value={resultForm.title} onChange={(event) => setResultForm((current) => ({ ...current, title: event.target.value }))} placeholder="成果标题" />
                <textarea value={resultForm.content} onChange={(event) => setResultForm((current) => ({ ...current, content: event.target.value }))} placeholder="说明你完成了什么、遇到什么问题、下一步准备做什么" />
                <input value={resultForm.url} onChange={(event) => setResultForm((current) => ({ ...current, url: event.target.value }))} placeholder="成果链接，可选" />
                <button className="primary-button" type="submit" disabled={saving || !resultForm.title.trim()}>
                  {saving ? <Loader2 size={16} /> : <Send size={16} />}
                  提交成果
                </button>
              </form>

              {(selected.results || []).length ? (
                <section className="project-results-list">
                  <h3>已提交成果</h3>
                  {selected.results.map((item) => (
                    <article key={item.id}>
                      <strong>{item.title}</strong>
                      <p>{item.content}</p>
                      {item.url ? <a href={item.url} target="_blank" rel="noreferrer">查看链接 <ExternalLink size={14} /></a> : null}
                    </article>
                  ))}
                </section>
              ) : null}
            </>
          ) : (
            <div className="empty-state-card">暂无项目数据。先从项目库或学习路线生成项目。</div>
          )}
        </section>
      </section>
    </main>
  )
}
